package br.com.heyagenda.data.remote

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.AuthResult
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flowOf

interface FirebaseAuthDataSource {
    val authStateChanged: Flow<FirebaseUser?>
    val currentFirebaseUser: FirebaseUser?
    suspend fun signIn(email: String, pass: String): AuthResult
    suspend fun createUser(email: String, pass: String): AuthResult
    suspend fun signInWithCredential(credential: dev.gitlive.firebase.auth.AuthCredential): AuthResult
    suspend fun signInWithGoogle(idToken: String, accessToken: String? = null): AuthResult
    suspend fun signInWithApple(idToken: String, rawNonce: String? = null, accessToken: String? = null): AuthResult
    suspend fun reloadUser(): FirebaseUser?
    suspend fun updateDisplayName(name: String)
    suspend fun sendPasswordResetEmail(email: String)
    suspend fun signOut()
    suspend fun getIdToken(forceRefresh: Boolean = false): String?
    suspend fun deleteCurrentUser()
    fun getAuthProvider(): String
    suspend fun reauthenticateWithPassword(password: String)
    suspend fun reauthenticateWithGoogle(idToken: String, accessToken: String? = null)
    suspend fun reauthenticateWithApple(idToken: String, rawNonce: String? = null, accessToken: String? = null)
}

class FirebaseAuthDataSourceImpl : FirebaseAuthDataSource {

    private val auth: FirebaseAuth? by lazy {
        try {
            Firebase.auth
        } catch (e: Throwable) {
            null
        }
    }

    override val authStateChanged: Flow<FirebaseUser?>
        get() = auth?.authStateChanged ?: flowOf(null)

    override val currentFirebaseUser: FirebaseUser?
        get() = auth?.currentUser

    override suspend fun signIn(email: String, pass: String): AuthResult {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        return instance.signInWithEmailAndPassword(email, pass)
    }

    override suspend fun createUser(email: String, pass: String): AuthResult {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        return instance.createUserWithEmailAndPassword(email, pass)
    }

    override suspend fun signInWithCredential(credential: dev.gitlive.firebase.auth.AuthCredential): AuthResult {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        return instance.signInWithCredential(credential)
    }

    override suspend fun signInWithGoogle(idToken: String, accessToken: String?): AuthResult {
        val credential = dev.gitlive.firebase.auth.GoogleAuthProvider.credential(
            idToken = idToken,
            accessToken = accessToken
        )
        return signInWithCredential(credential)
    }

    override suspend fun signInWithApple(idToken: String, rawNonce: String?, accessToken: String?): AuthResult {
        val credential = dev.gitlive.firebase.auth.OAuthProvider.credential(
            providerId = "apple.com",
            idToken = idToken,
            rawNonce = rawNonce,
            accessToken = accessToken
        )
        return signInWithCredential(credential)
    }

    override suspend fun reloadUser(): FirebaseUser? {
        return try {
            auth?.currentUser?.reload()
            auth?.currentUser
        } catch (_: Throwable) {
            auth?.currentUser
        }
    }

    override suspend fun updateDisplayName(name: String) {
        try {
            auth?.currentUser?.updateProfile(displayName = name)
        } catch (e: Throwable) {
            // Non-blocking updateProfile
        }
    }

    override suspend fun sendPasswordResetEmail(email: String) {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        instance.sendPasswordResetEmail(email.trim())
    }

    override suspend fun signOut() {
        auth?.signOut()
    }

    override suspend fun getIdToken(forceRefresh: Boolean): String? {
        return try {
            auth?.currentUser?.getIdToken(forceRefresh)
        } catch (_: Throwable) {
            null
        }
    }

    override suspend fun deleteCurrentUser() {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        val user = instance.currentUser ?: throw IllegalStateException("Usuário não autenticado.")
        user.delete()
    }

    override fun getAuthProvider(): String {
        val providers = auth?.currentUser?.providerData?.map { it.providerId } ?: emptyList()
        return when {
            providers.any { it.contains("google", ignoreCase = true) } -> "google"
            providers.any { it.contains("apple", ignoreCase = true) } -> "apple"
            else -> "password"
        }
    }

    override suspend fun reauthenticateWithPassword(password: String) {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        val user = instance.currentUser ?: throw IllegalStateException("Usuário não autenticado.")
        val email = user.email ?: throw IllegalStateException("E-mail não encontrado.")
        val credential = dev.gitlive.firebase.auth.EmailAuthProvider.credential(
            email = email,
            password = password
        )
        user.reauthenticate(credential)
    }

    override suspend fun reauthenticateWithGoogle(idToken: String, accessToken: String?) {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        val user = instance.currentUser ?: throw IllegalStateException("Usuário não autenticado.")
        val credential = dev.gitlive.firebase.auth.GoogleAuthProvider.credential(
            idToken = idToken,
            accessToken = accessToken
        )
        user.reauthenticate(credential)
    }

    override suspend fun reauthenticateWithApple(idToken: String, rawNonce: String?, accessToken: String?) {
        val instance = auth ?: throw IllegalStateException("Firebase Auth não inicializado.")
        val user = instance.currentUser ?: throw IllegalStateException("Usuário não autenticado.")
        val credential = dev.gitlive.firebase.auth.OAuthProvider.credential(
            providerId = "apple.com",
            idToken = idToken,
            rawNonce = rawNonce,
            accessToken = accessToken
        )
        user.reauthenticate(credential)
    }
}
